import { useContext, useMemo, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { FileImportPanel } from '@/components/FileImport/FileImportPanel';
import Loader from '@/components/Loader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTabLabels } from '@/hooks/useTabLabels';
import { GlobalModalStyle } from '@/Shared/StyledElements';
import { useCourseEditStore } from '@/stores/courseEditStore';

import CommentDrawer, { CommentDrawerProvider } from './CourseComments/CommentDrawer';
import CourseComments from './CourseComments/CourseComments';
import CourseDescriptionTab from './CourseDescriptionTab/CourseDescriptionTab';
import { StyledCourseContainer, StyledFade, StyledFooterBody } from './CourseEdit.Styles';
import CourseEditContext from './CourseEditContext';
import CourseEditSubmit from './CourseEditSubmit/CourseEditSubmit';
import CourseFooter from './CourseFooter/CourseFooter';
import CourseHeader from './CourseHeader/CourseHeader';
import CourseLessonPlans from './CourseLessonPlansTab/CourseLessonPlans';
import LessonPlanFooter from './CourseLessonPlansTab/LessonPlanFooter';
import LessonPlansPanel from './CourseLessonPlansTab/LessonPlansPanel';
import CourseOutcomeTab from './CourseOutcomeTab/CourseOutcomeTab';
import CourseResourcesTab from './CourseResourcesTab/CourseResourcesTab';
import CourseUnitsTab from './CourseUnits/CourseUnitsTab';

const TAB_VALUES = [
	'description',
	'outcomes',
	'resources',
	'units-of-focus',
	'lesson-plans',
] as const;

const CourseEdit = () => {
	const {
		canEdit,
		courseData,
		isAlignment,
		isApprovalMode,
		isFetched,
		isFetchedAfterMount,
		saveDraft,
		submitCourse,
	} = useContext(CourseEditContext);
	const { commentTypes } = useCourseEditStore();
	const { handleSubmit } = useForm();
	const [driveImportOpen, setDriveImportOpen] = useState(false);
	const location = useLocation();
	const navigate = useNavigate();
	const labels = useTabLabels();

	const activeTab = useMemo(() => {
		const hash = location.hash.slice(1).toLowerCase();
		if (!hash) return 'description';
		const valid = isApprovalMode ? TAB_VALUES.filter((v) => v !== 'lesson-plans') : [...TAB_VALUES];
		return valid.includes(hash as (typeof TAB_VALUES)[number]) ? hash : 'description';
	}, [location.hash, isApprovalMode]);

	const handleTabChange = (value: string) => {
		if (location.pathname.includes('courses') || location.pathname.includes('submissions')) {
			navigate(`${location.pathname}${location.search}#${value}`, { replace: true });
		}
	};

	const onSubmit = () => {
		submitCourse();
	};

	const onDraftSubmit = () => {
		saveDraft();
	};

	if (!(isFetched && courseData && isFetchedAfterMount)) {
		return <Loader size="small" />;
	}

	return (
		<CommentDrawerProvider>
			<StyledFade>
				<GlobalModalStyle />
				<FileImportPanel open={driveImportOpen} onClose={() => setDriveImportOpen(false)} />
				<StyledCourseContainer>
					<div className="course-content">
						<CourseHeader
							onImportClick={
								canEdit && !isApprovalMode ? () => setDriveImportOpen(true) : undefined
							}
						/>
						<Tabs value={activeTab} onValueChange={handleTabChange}>
							<TabsList variant="line" className="w-full">
								<TabsTrigger variant="line" value="description">
									{labels.description}
								</TabsTrigger>
								<TabsTrigger variant="line" value="outcomes">
									{labels.outcomes}
								</TabsTrigger>
								<TabsTrigger variant="line" value="resources">
									{labels.resources}
								</TabsTrigger>
								<TabsTrigger variant="line" value="units-of-focus">
									{labels.units}
								</TabsTrigger>
								{!isApprovalMode && (
									<TabsTrigger variant="line" value="lesson-plans">
										{labels.lessonPlans}
									</TabsTrigger>
								)}
							</TabsList>
							<TabsContent value="description" className="pl-0">
								<CourseDescriptionTab />
							</TabsContent>
							<TabsContent value="outcomes" className="pl-0">
								<CourseOutcomeTab />
							</TabsContent>
							<TabsContent value="resources" className="pl-0">
								<CourseResourcesTab />
							</TabsContent>
							<TabsContent value="units-of-focus" className="pl-0">
								<CourseUnitsTab />
							</TabsContent>
							{!isApprovalMode && (
								<TabsContent value="lesson-plans" className="pl-0">
									<CourseLessonPlans />
								</TabsContent>
							)}
						</Tabs>
					</div>

					{/* Desktop: inline side panel (unchanged behaviour) */}
					<div className="comment-panel hidden md:block">
						{!commentTypes.includes('none') && <CourseComments />}
						{commentTypes.includes('none') && <LessonPlansPanel />}
					</div>

					{/* Mobile: floating FAB + slide-in drawer */}
					<div className="md:hidden">
						<CommentDrawer />
					</div>
				</StyledCourseContainer>
				<StyledFooterBody>
					{canEdit && !isApprovalMode && !isAlignment && !commentTypes.includes('none') && (
						<CourseEditSubmit
							submitEvent={handleSubmit(onSubmit)}
							draftSubmitEvent={handleSubmit(onDraftSubmit)}
						/>
					)}
					{isApprovalMode && !commentTypes.includes('none') && <CourseFooter />}
					{commentTypes.includes('none') && <LessonPlanFooter />}
				</StyledFooterBody>
			</StyledFade>
		</CommentDrawerProvider>
	);
};

export default CourseEdit;
